// Turn-based combat exchange. Faithful (simplified) port of the legacy
// combat.func.php formula. Pure & deterministic; returns a neutral
// ExchangeResult the upper layer localizes.

import {
  DAMAGE_SCALE,
  FISTS,
  HIT_BASE,
  INJURY_PARTS,
  INJURY_RATE,
  rangeCompatible,
  SKILL_DMG,
} from './JKGame_content'
import type { BodyPart, Entity, ExchangeResult, Weapon } from './JKGame_types'
import type { JKGameRng } from './JKGame_rng'

function weaponAtt(w: Weapon): number {
  if (w.kind === 'N') return 0
  // legacy: melee watt = wepe*2, ranged watt = wepe
  return w.kind === 'P' || w.kind === 'K' ? w.power * 2 : w.power
}

function injuryFactor(e: Entity): number {
  return Math.max(0.5, 1 - 0.08 * e.injuries.size)
}

function attackPower(e: Entity): number {
  return Math.max(1, (e.att + weaponAtt(e.weapon)) * injuryFactor(e))
}

function defensePower(e: Entity): number {
  return Math.max(1, (e.def + e.armor) * injuryFactor(e))
}

function hitChance(e: Entity): number {
  return Math.min(98, HIT_BASE[e.weapon.kind] + e.skill * 0.2)
}

// PARITY: Python round() is banker's rounding (half-to-EVEN); JS Math.round is half-UP.
// With DAMAGE_SCALE=0.5, damage lands on X.5 constantly, so they diverge (e.g. 12.5 → 12 not 13).
// This mirrors combat.py's round() exactly. dmg is always positive here, so floor-based is safe.
function roundHalfEven(x: number): number {
  const f = Math.floor(x)
  const diff = x - f
  if (diff < 0.5) return f
  if (diff > 0.5) return f + 1
  return f % 2 === 0 ? f : f + 1 // exact .5 → nearest even
}

function consumeAmmo(e: Entity): void {
  if (e.weapon.ammo < 0) return // ∞ (空手 / 球棒)
  const left = e.weapon.ammo - 1
  e.weapon = left <= 0 ? { ...FISTS } : { ...e.weapon, ammo: left }
}

interface Strike {
  hit: boolean
  dmg: number
  crit: boolean
  injury: BodyPart | null
}

function strike(attacker: Entity, defender: Entity, rng: JKGameRng, mult = 1): Strike {
  const consumesAmmo = attacker.weapon.ammo >= 0
  // PARITY: the backend (source of truth) floors the hit chance — combat.py:51 does
  // `rng.pct(int(_hit_chance(att)))`. hitChance is always positive, so Math.floor == int().
  // Must stay floored to keep the boundary d100 roll 1:1 with the Python sim.
  if (!rng.pct(Math.floor(hitChance(attacker)))) {
    if (consumesAmmo) consumeAmmo(attacker)
    return { hit: false, dmg: 0, crit: false, injury: null }
  }

  const ratio = attackPower(attacker) / defensePower(defender)
  let dmg = ratio * attacker.skill * SKILL_DMG[attacker.weapon.kind] * DAMAGE_SCALE
  // PARITY: roundHalfEven mirrors Python round() (combat.py:58-59,65,70). See helper above.
  const lo = Math.max(1, roundHalfEven(dmg * 0.3))
  const hi = Math.max(lo, roundHalfEven(dmg))
  dmg = rng.int(lo, hi)

  let crit = false
  if (attacker.rage >= 30 && rng.pct(28)) {
    crit = true
    dmg = roundHalfEven(dmg * 1.4)
    attacker.rage -= 30
  }
  if (attacker.weapon.kind === 'D') dmg += attacker.weapon.power
  if (mult > 1) {
    dmg = roundHalfEven(dmg * mult) // special move
    crit = true
  }
  dmg = Math.max(1, dmg)

  let injury: BodyPart | null = null
  const parts = INJURY_PARTS[attacker.weapon.kind]
  if (parts.length > 0 && rng.pct(INJURY_RATE[attacker.weapon.kind])) {
    const p = rng.pick(parts)
    if (!defender.injuries.has(p)) injury = p
  }

  // use-based growth (legacy: wep skill ++ each swing)
  attacker.skill += 1
  attacker.rage = Math.min(100, attacker.rage + 6)
  if (consumesAmmo) consumeAmmo(attacker)

  return { hit: true, dmg, crit, injury }
}

function applyDeath(victim: Entity, killer: Entity, res: ExchangeResult): void {
  victim.hp = 0
  victim.alive = false
  killer.kills += 1
  // Passive first_blood: 首殺後怒氣 +50（loadout side-grade，數值持平 → PvE 差異化、不破壞公平）。
  // 只在「該角色的第一個擊殺」觸發；rage 上限同 strike() 的 100。
  if (killer.passive === 'first_blood' && killer.kills === 1) {
    killer.rage = Math.min(100, killer.rage + 50)
  }
  if (victim.hasKey) {
    victim.hasKey = false
    killer.hasKey = true
    res.keyTaken = true
  }
}

/**
 * One full exchange: `att` strikes `def`; if `def` survives and ranges are
 * compatible, `def` may counter. Mutates both entities' hp/skill/rage/injuries
 * and weapon ammo. Sets alive=false on death (deathStep is stamped by match).
 */
export function exchange(att: Entity, def: Entity, rng: JKGameRng, powerMult = 1): ExchangeResult {
  const res: ExchangeResult = {
    attackerName: att.name,
    defenderName: def.name,
    attHit: false,
    attDmg: 0,
    attCrit: false,
    attInjury: null,
    countered: false,
    defDmg: 0,
    defInjury: null,
    defenderDied: false,
    attackerDied: false,
    keyTaken: false,
  }

  const a = strike(att, def, rng, powerMult)
  res.attHit = a.hit
  res.attDmg = a.dmg
  res.attCrit = a.crit
  res.attInjury = a.injury
  if (a.hit) {
    def.hp -= a.dmg
    if (a.injury) def.injuries.add(a.injury)
  }
  if (def.hp <= 0) {
    res.defenderDied = true
    applyDeath(def, att, res)
    return res
  }

  if (rangeCompatible(att.weapon.range, def.weapon.range) && rng.pct(50)) {
    res.countered = true
    const c = strike(def, att, rng)
    res.defDmg = c.dmg
    res.defInjury = c.injury
    if (c.hit) {
      att.hp -= c.dmg
      if (c.injury) att.injuries.add(c.injury)
    }
    if (att.hp <= 0) {
      res.attackerDied = true
      applyDeath(att, def, res)
    }
  }
  return res
}
